import axios from 'axios';
import api from './axiosInstance';

/**
 * File Service (S3)
 *
 * Upload flow (preferred — no AWS keys required on the API server beyond
 * signing rights via its IAM role):
 *   1. POST /files/presign-upload { fileName, contentType }
 *      -> { key, uploadUrl, publicUrl }
 *   2. PUT <uploadUrl> with the file body (direct to S3, no auth header)
 *   3. Store `key` or `publicUrl`. Use `resolveMediaUrl` / getSignedUrl to view.
 *
 * View flow: backend stores the S3 object key; we resolve it to a short-lived
 * signed URL via GET /files/url.
 */

type GetFileUrlResponse = { url: string };

type PresignUploadResponse = {
  key: string;
  uploadUrl: string;
  publicUrl: string;
  contentType: string;
  expiresIn: number;
};

export interface UploadedFile {
  key: string;
  /** Canonical (unsigned) S3 object URL — suitable to persist to the database. */
  url: string;
}

function extractS3ObjectKey(rawUrl: string): string | null {
  const trimmed = rawUrl.trim();
  try {
    const u = new URL(trimmed);
    const host = u.hostname.toLowerCase();
    const virtualHosted = /^(.+)\.s3\.([a-z0-9-]+)\.amazonaws\.com$/i.exec(host);
    if (virtualHosted) {
      const path = u.pathname.startsWith('/') ? u.pathname.slice(1) : u.pathname;
      if (!path) return null;
      return decodeURIComponent(path);
    }
    const pathStyle = /^s3\.([a-z0-9-]+)\.amazonaws\.com$/i.exec(host);
    if (pathStyle) {
      const parts = u.pathname.split('/').filter(Boolean);
      if (parts.length < 2) return null;
      return decodeURIComponent(parts.slice(1).join('/'));
    }
    return null;
  } catch {
    return null;
  }
}

function isBareObjectKey(value: string): boolean {
  const v = value.trim();
  if (!v || v.startsWith('http://') || v.startsWith('https://')) return false;
  return v.startsWith('uploads/');
}

function isAlreadyPresignedAwsUrl(value: string): boolean {
  return (
    value.includes('X-Amz-Algorithm=') ||
    value.includes('X-Amz-Credential=') ||
    value.includes('AWSAccessKeyId=')
  );
}

/** True if URL or path looks like a raster image (thumbnail / preview). */
export function looksLikeImageMediaUrl(url: string | undefined | null): boolean {
  if (!url) return false;
  return /\.(jpe?g|png|gif|webp)(\?|#|$)/i.test(url.trim());
}

/**
 * True if the value refers to a stored S3 object that must be resolved to a
 * signed URL before rendering (bare object key, or canonical S3 URL).
 * Returns false for data:/blob: URIs, already-presigned URLs, and other http(s) URLs.
 */
export function isStoredObjectKey(value: string | null | undefined): boolean {
  if (!value) return false;
  const v = value.trim();
  if (!v) return false;
  if (v.startsWith('data:') || v.startsWith('blob:')) return false;
  if (isAlreadyPresignedAwsUrl(v)) return false;
  if (isBareObjectKey(v)) return true;
  return extractS3ObjectKey(v) !== null;
}

export interface UploadOptions {
  /** Progress callback, 0..1. */
  onProgress?: (fraction: number) => void;
  /** Override inferred content type. */
  contentType?: string;
  /** Abort signal to cancel the PUT. */
  signal?: AbortSignal;
}

export const fileService = {
  /**
   * Upload a file directly to S3 using a presigned PUT URL.
   * Returns the stored object `key` and its canonical (unsigned) `url`.
   */
  async uploadFile(file: File, options: UploadOptions = {}): Promise<UploadedFile> {
    const contentType = options.contentType || file.type || 'application/octet-stream';

    const presignResp = await api.post('/files/presign-upload', {
      fileName: file.name,
      contentType,
    });

    const presign = (presignResp.data as { data: PresignUploadResponse }).data;
    if (!presign?.uploadUrl || !presign?.key) {
      throw new Error('Failed to get S3 upload URL');
    }

    await axios.put(presign.uploadUrl, file, {
      headers: { 'Content-Type': contentType },
      // S3 PUT must NOT include cookies/auth; use a bare axios (not `api`).
      withCredentials: false,
      signal: options.signal as any,
      onUploadProgress: (evt) => {
        if (!options.onProgress) return;
        const total = evt.total ?? file.size;
        if (total > 0) options.onProgress(Math.min(1, evt.loaded / total));
      },
    });

    return { key: presign.key, url: presign.publicUrl };
  },

  async getSignedUrl(key: string): Promise<string> {
    const normalizedKey = key.startsWith('/') ? key.slice(1) : key;
    const response = await api.get(`/files/url`, { params: { key: normalizedKey } });

    const payload = (response.data as { data: GetFileUrlResponse }).data;
    return payload.url;
  },

  /** Delete object by full S3 key (use query param; keys may contain `/`). */
  async deleteFile(key: string): Promise<void> {
    const normalizedKey = key.startsWith('/') ? key.slice(1) : key;
    await api.delete('/files', { params: { key: normalizedKey } });
  },

  /**
   * Returns a URL suitable for <img src> — presigns private S3 object URLs and raw keys; leaves external URLs unchanged.
   */
  async resolveMediaUrl(raw: string): Promise<string> {
    const value = (raw || '').trim();
    if (!value) return value;
    if (value.startsWith('data:') || value.startsWith('blob:')) return value;
    if (isAlreadyPresignedAwsUrl(value)) return value;

    const keyFromUrl = extractS3ObjectKey(value);
    const key = keyFromUrl ?? (isBareObjectKey(value) ? value.replace(/^\/+/, '') : null);
    if (!key) return value;

    return fileService.getSignedUrl(key);
  },

  /**
   * Opens stored media (canonical S3 URL, object key, presigned URL, or external URL) in a new tab.
   * Use for Preview / Download when the bucket is private.
   */
  async openMediaInNewTab(raw: string | undefined | null): Promise<void> {
    const value = (raw || '').trim();
    if (!value) return;
    const url = await fileService.resolveMediaUrl(value);
    const opened = window.open(url, '_blank', 'noopener,noreferrer');
    if (!opened) {
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  },
};
